import {
  BankIcon,
  BinocularsIcon,
  BriefcaseIcon,
  CertificateIcon,
  ChartBarIcon,
  ChatCircleIcon,
  CompassIcon,
  GearIcon,
  Icon,
  LifebuoyIcon,
  NetworkIcon,
  NotepadIcon,
  PresentationChartIcon,
  UsersIcon,
  UsersThreeIcon,
  VaultIcon,
} from '@phosphor-icons/react';
import {
  FundStatusEnumType,
  SyndicateStatusEnumType,
  SyndicateTypeEnumType,
} from '__generated__/graphql';

interface TabType {
  key: string;
  label: string;
  icon: Icon;
  route: string;
  exact?: boolean;
  disabled?: boolean;
  trailingIcon?: React.ReactNode;
}

const getInvestorTabs: () => TabType[] = () => [
  {
    key: 'accreditation',
    label: 'Accreditation',
    icon: CertificateIcon,
    route: '/invest/accreditation',
  },
  {
    key: 'invest-hub',
    label: 'Invest',
    icon: CompassIcon,
    route: '/invest',
    trailingIcon: (
      <div className="px-1 bg-orange-400 rounded-sm -skew-x-6 text-white text-xs">NEW</div>
    ),
  },
  {
    key: 'portfolio',
    label: 'Portfolio',
    icon: ChartBarIcon,
    route: '/invest/portfolio',
    exact: true,
  },
  {
    key: 'syndicates',
    label: 'Syndicates',
    icon: UsersThreeIcon,
    route: '/invest/syndicates',
  },
  {
    key: 'documents',
    label: 'Documents',
    icon: VaultIcon,
    route: '/invest/documents',
  },
  {
    key: 'wishlist',
    label: 'Wishlist',
    icon: BinocularsIcon,
    route: '/invest/wishlist',
  },
  {
    key: 'conversations',
    label: 'Messages',
    icon: ChatCircleIcon,
    route: '/invest/conversations',
  },
  {
    key: 'posts',
    label: 'Posts',
    icon: NotepadIcon,
    route: `/invest/posts`,
  },
  // {
  //   key: 'fund-posts',
  //   label: 'Fund Posts',
  //   icon: notepadIcon,
  //   route: `/invest/fund-posts`,
  // },
  {
    key: 'investment-entities',
    label: 'Investment Entities',
    icon: BankIcon,
    route: '/invest/investment-entities',
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: GearIcon,
    route: '/invest/settings',
  },
  {
    key: 'help',
    label: 'Help',
    icon: LifebuoyIcon,
    route: `/invest/help`,
  },
];

const getSyndicateDashboardTabs: (
  syndicateId: string,
  status?: SyndicateStatusEnumType,
  syndicateType?:
    | SyndicateTypeEnumType.Default
    | SyndicateTypeEnumType.Ruv
    | SyndicateTypeEnumType.Fund
    | SyndicateTypeEnumType.CoSyndicate
) => TabType[] = (syndicateId, status, syndicateType) => {
  return [
    {
      key: 'portfolio',
      label: 'Portfolio',
      icon: ChartBarIcon,
      route: `/syndicate-dashboard/${syndicateId}`,
      exact: true,
    },
    {
      key: 'analytics',
      label: 'Analytics',
      icon: PresentationChartIcon,
      route: `/syndicate-dashboard/${syndicateId}/analytics`,
      disabled: status !== SyndicateStatusEnumType.Active,
    },
    {
      key: 'deals',
      label: syndicateType === SyndicateTypeEnumType.Ruv ? 'RUVs' : 'Deals',
      icon: BriefcaseIcon,
      route: `/syndicate-dashboard/${syndicateId}/deals`,
      disabled: status !== SyndicateStatusEnumType.Active,
    },
    {
      key: 'network',
      label: 'Network',
      icon: NetworkIcon,
      route: `/syndicate-dashboard/${syndicateId}/network`,
      disabled: status !== SyndicateStatusEnumType.Active,
    },
    {
      key: 'company-events',
      label: 'Company Events',
      icon: NotepadIcon,
      route: `/syndicate-dashboard/${syndicateId}/company-events`,
      disabled: status !== SyndicateStatusEnumType.Active,
    },
    {
      key: 'team',
      label: 'Team',
      icon: UsersIcon,
      route: `/syndicate-dashboard/${syndicateId}/team`,
      disabled: status !== SyndicateStatusEnumType.Active,
    },
    {
      key: 'conversations',
      label: 'Messages',
      icon: ChatCircleIcon,
      route: `/syndicate-dashboard/${syndicateId}/conversations`,
      disabled: status !== SyndicateStatusEnumType.Active,
    },
    {
      key: 'posts',
      label: 'Posts',
      icon: NotepadIcon,
      route: `/syndicate-dashboard/${syndicateId}/posts`,
      disabled: status !== SyndicateStatusEnumType.Active,
    },
    {
      key: 'update-requests',
      label: 'Update Requests',
      icon: NotepadIcon,
      route: `/syndicate-dashboard/${syndicateId}/update-requests`,
      disabled: status !== SyndicateStatusEnumType.Active,
    },
    {
      key: 'settings',
      label: 'Settings',
      icon: GearIcon,
      route: `/syndicate-dashboard/${syndicateId}/settings`,
    },
    {
      key: 'help',
      label: 'Help',
      icon: LifebuoyIcon,
      route: `/syndicate-dashboard/${syndicateId}/help`,
    },
  ];
};

const getRuvDashboardTabs: (syndicateId: string, status?: SyndicateStatusEnumType) => TabType[] = (
  syndicateId,
  status
) => [
  {
    key: 'deals',
    label: 'RUVs',
    icon: BriefcaseIcon,
    route: `/syndicate-dashboard/${syndicateId}/deals`,
  },
  {
    key: 'network',
    label: 'Network',
    icon: NetworkIcon,
    route: `/syndicate-dashboard/${syndicateId}/network`,
    disabled: status !== SyndicateStatusEnumType.Active,
  },
  // {
  //   key: 'conversations',
  //   label: 'Messages',
  //   icon: messageIcon,
  //   route: `/syndicate-dashboard/${syndicateId}/conversations`,
  // },
  // {
  //   key: 'posts',
  //   label: 'Posts',
  //   icon: notepadIcon,
  //   route: `/syndicate-dashboard/${syndicateId}/posts`,
  // },
  {
    key: 'update-requests',
    label: 'Update Requests',
    icon: NotepadIcon,
    route: `/syndicate-dashboard/${syndicateId}/update-requests`,
    disabled: status !== SyndicateStatusEnumType.Active,
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: GearIcon,
    route: `/syndicate-dashboard/${syndicateId}/settings`,
  },
  {
    key: 'help',
    label: 'Help',
    icon: LifebuoyIcon,
    route: `/syndicate-dashboard/${syndicateId}/help`,
  },
];

const getFundDashboardTabs: (
  fundId: string,
  status?: FundStatusEnumType,
  isNewFund?: boolean
) => TabType[] = (fundId, status, isNewFund = false) => {
  const baseTabs = [
    {
      key: 'portfolio',
      label: 'Overview',
      icon: ChartBarIcon,
      route: `/fund-dashboard/${fundId}`,
      exact: true,
    },
    // {
    //   key: 'network',
    //   label: 'Network',
    //   icon: networkIcon,
    //   route: `/fund-dashboard/${fundId}/network`,
    // },
    {
      key: 'investments',
      label: 'Portfolio',
      icon: BriefcaseIcon,
      route: `/fund-dashboard/${fundId}/investments`,
      disabled: status !== FundStatusEnumType.Active,
    },
    {
      key: 'partners',
      label: 'Limited Partners',
      icon: NetworkIcon,
      route: `/fund-dashboard/${fundId}/partners`,
      disabled: status !== FundStatusEnumType.Active,
    },
    {
      key: 'deals',
      label: 'Deals',
      icon: BriefcaseIcon,
      route: `/fund-dashboard/${fundId}/deals-of-fund`,
    },
    {
      key: 'posts',
      label: 'Posts',
      icon: NotepadIcon,
      route: `/fund-dashboard/${fundId}/posts`,
      disabled: status !== FundStatusEnumType.Active,
    },
    {
      key: 'settings',
      label: 'Settings',
      icon: GearIcon,
      route: `/fund-dashboard/${fundId}/settings`,
    },
  ];

  if (!isNewFund) {
    return baseTabs.filter(tab => !['deals'].includes(tab.key));
  }

  return baseTabs;
};

// const getSidebarTabsForProfile = (
//   profileType: 'invest' | 'syndicate-dashboard' | 'fund-dashboard',
//   syndicateId?: string | null,
//   fundId?: string | null
// ) => {
//   if (profileType === 'syndicate-dashboard') return syndicateDashboardTabs(syndicateId || '');
//   else if (profileType === 'fund-dashboard') return fundDashboardTabs(fundId || '');
//   return investorTabs;
// };

export { getInvestorTabs, getSyndicateDashboardTabs, getFundDashboardTabs, getRuvDashboardTabs };
